import React, { useEffect, useMemo, useRef, useState } from "react";
import "../common-form-styles.css";
import { FIELD_TYPES, camelCaseToNormalCase, formatInferredValue, isObject, } from "../../../../shared";
import { goToNext, goPrevious, useFieldAutoFocus, useFormValue } from "../common";
import { useFluidForm } from "../FormContext";
import { FormLabel } from "./FormLabel";
import { InputRegion, TextAreaRegion } from "./FieldText.component";
import ValidationIcon from "./ValidationIcon";
function isNonEmptyStructuredValue(v) {
    if (v === null || v === undefined)
        return false;
    if (typeof v === "string")
        return v.trim().length > 0;
    if (typeof v === "number")
        return true;
    if (typeof v === "boolean")
        return true;
    if (Array.isArray(v))
        return v.length > 0;
    if (typeof v === "object")
        return Object.keys(v).length > 0;
    return String(v).trim().length > 0;
}
export function FieldInputCompactComponent({ fieldPath, width, options, validationType, }) {
    const fluidForm = useFluidForm();
    const field = fluidForm.getFieldRenderView(fieldPath);
    const config = fluidForm.getFieldConfigByFieldPath(fieldPath);
    const ref = useRef(null);
    const readOnly = config?.readOnly || false;
    const omitLabel = options?.omitLabel || false;
    const [value, setValue] = useFormValue(field.fieldPath);
    useFieldAutoFocus(ref, field.fieldPath, options?.autoFocus);
    const structuredEnabled = isObject(value) &&
        options?.useStructuredOutput &&
        typeof FIELD_TYPES[config.type].parts !== "string";
    const [isStructuredEditing, setIsStructuredEditing] = useState(false);
    useEffect(() => {
        if (!structuredEnabled && isStructuredEditing) {
            setIsStructuredEditing(false);
        }
    }, [structuredEnabled, isStructuredEditing]);
    const allKeys = useMemo(() => {
        if (!structuredEnabled)
            return [];
        const parts = FIELD_TYPES[config.type].parts;
        return Object.keys(parts);
    }, [structuredEnabled, config.type]);
    const structuredSummaryItems = useMemo(() => {
        if (!structuredEnabled)
            return [];
        const v = value;
        const items = allKeys
            .map((k) => {
            const partVal = v?.[k];
            if (!isNonEmptyStructuredValue(partVal))
                return null;
            return { key: k, label: camelCaseToNormalCase(k), value: String(partVal) };
        })
            .filter(Boolean);
        return items;
    }, [structuredEnabled, value, allKeys]);
    const handleKeyDown = (event) => {
        const fieldName = event.target.getAttribute("data-fieldName");
        if (event.key === "Tab") {
            event.preventDefault();
            goToNext(fieldName, fluidForm);
            return;
        }
        if (event.key === "Backspace" && event.target.value === "") {
            event.preventDefault();
            goPrevious(fieldName, fluidForm);
            return;
        }
    };
    const handleBlur = () => {
        fluidForm.unfocusField(field.fieldPath);
    };
    const formattedMainValue = isObject(value) ? formatInferredValue(value, config) : (value || "");
    const openStructuredEdit = () => {
        if (!structuredEnabled)
            return;
        setIsStructuredEditing(true);
    };
    const closeStructuredEdit = () => {
        setIsStructuredEditing(false);
    };
    const handleStructuredContainerBlur = (e) => {
        const nextFocused = e.relatedTarget;
        const container = e.currentTarget;
        if (!nextFocused || !container.contains(nextFocused)) {
            fluidForm.unfocusField(field.fieldPath);
            //fluidForm.updateDefaultBindings(field);
        }
    };
    const handleStructuredContainerFocus = () => {
        fluidForm.setCurrentField(field.fieldPath);
    };
    const renderStructuredCompactSummary = () => {
        if (!structuredEnabled)
            return null;
        if (!structuredSummaryItems.length)
            return null;
        if (isStructuredEditing)
            return null;
        return (React.createElement("div", { role: "button", tabIndex: 0, className: "mt-1.5 text-xs cursor-pointer select-none", onMouseDown: (e) => {
                // keep main input from blurring on click
                e.preventDefault();
            }, onClick: openStructuredEdit, onKeyDown: (e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openStructuredEdit();
                }
            }, "aria-label": "Edit structured data" },
            React.createElement("div", { className: "flex items-start justify-between gap-2" },
                React.createElement("div", { className: "min-w-0" },
                    React.createElement("div", { className: "truncate text-gray-600 ml-3" }, structuredSummaryItems.map((item, idx) => (React.createElement(React.Fragment, { key: item.key },
                        idx > 0 ? React.createElement("span", { className: "text-gray-300" }, " \u00B7 ") : null,
                        React.createElement("span", { className: "text-gray-400" }, item.label),
                        React.createElement("span", { className: "text-gray-400" }, ": "),
                        React.createElement("span", { className: "text-gray-800" }, item.value)))))),
                React.createElement("div", { className: "shrink-0 text-[11px] font-medium text-gray-400 hover:text-gray-600" }, "Edit"))));
    };
    const renderStructuredEdit = () => {
        if (!structuredEnabled)
            return null;
        if (!isStructuredEditing)
            return null;
        const v = value;
        return (React.createElement("div", { className: "mt-2 rounded-md border border-gray-200 bg-white p-3", onBlur: handleStructuredContainerBlur, onFocus: handleStructuredContainerFocus },
            React.createElement("div", { className: "mb-2 flex items-center justify-between" },
                React.createElement("div", { className: "text-xs font-medium text-gray-700" }, "Structured details"),
                React.createElement("button", { type: "button", className: "text-xs font-medium text-gray-500 hover:text-gray-700", onMouseDown: (e) => e.preventDefault(), onClick: closeStructuredEdit }, "Close")),
            React.createElement("div", { className: "flex flex-wrap gap-3" }, allKeys.map((key) => (React.createElement("div", { key: key, className: "flex flex-col gap-1 flex-1 min-w-36" },
                React.createElement(InputRegion, { type: "text", validationType: validationType, value: v?.[key] ?? "", onChange: (e) => setValue({ ...(v || {}), [key]: e.target.value }), className: "px-3 py-2 border border-gray-200 rounded text-sm bg-white text-gray-800 focus:border-gray-400 focus:outline-none transition-colors duration-150 w-full", placeholder: camelCaseToNormalCase(key) }),
                React.createElement("label", { className: "text-[11px] font-semibold text-gray-400 whitespace-nowrap ml-1" }, camelCaseToNormalCase(key))))))));
    };
    return (React.createElement(React.Fragment, null,
        !omitLabel && React.createElement(FormLabel, { config: config, options: options, fieldPath: fieldPath }),
        config.longText ? (React.createElement("div", { className: "flex flex-col" },
            React.createElement("div", { className: "flex" },
                React.createElement("div", { className: "flex-1 min-w-0", style: { width: width ? `${width}px` : undefined } },
                    React.createElement(TextAreaRegion, { "data-fieldname": field.fieldPath, isFocused: fluidForm.isFocused(field.fieldPath), suppressContentEditableWarning: true, onFocus: () => fluidForm.setCurrentField(field.fieldPath), onBlur: handleBlur, onKeyDown: handleKeyDown, onChange: (event) => {
                            setValue(event.target.value);
                        }, value: formattedMainValue, readOnly: readOnly, validationType: validationType }),
                    renderStructuredCompactSummary(),
                    renderStructuredEdit()),
                validationType && React.createElement(ValidationIcon, { validationType: validationType })))) : (React.createElement("div", { className: "flex flex-col" },
            React.createElement("div", { className: "flex" },
                React.createElement("div", { className: "flex-1 min-w-0", style: { width: width ? `${width}px` : undefined } },
                    React.createElement(InputRegion, { ref: ref, name: field.fieldPath, id: `ff-${field.fieldPath}`, type: "text", "data-fieldname": field.fieldPath, isFocused: fluidForm.isFocused(field.fieldPath), suppressContentEditableWarning: true, onFocus: () => fluidForm.setCurrentField(field.fieldPath), onBlur: handleBlur, width: width, onKeyDown: handleKeyDown, onChange: (event) => setValue(event.target.value), value: formattedMainValue, readOnly: readOnly, validationType: validationType }),
                    renderStructuredCompactSummary(),
                    renderStructuredEdit()),
                validationType && React.createElement(ValidationIcon, { validationType: validationType }))))));
}
